import {useCallback, useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Button} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Country} from "@pg-rp/common-types";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {useElementInteractionObserver} from "../../hooks/use_element_interaction_observer";
import {useGetOfferDetailTransportPoiQuery} from "../../maps/api/get_offer_detail_poi";
import {OpenStreetMapsWithPoi} from "../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {POI_DISTANCE_DEFAULT_VALUE_IN_METERS} from "../../maps/constants/pois_defaults";
import {useMapNearbyOffersPois} from "../../maps/hooks/use_map_nearby_offers_pois";
import {EMPTY_TRANSPORT_POIS, filterTransportPoisByRadius, useTransportPois} from "../../maps/hooks/use_transport_pois";
import {poiAnalytics, PoiGTMModalAction} from "../../maps/tracking/poi_analytics";
import {trackAmplitudeMapPoiClick} from "../../maps/tracking/track_amplitude_poi_click";
import {IPoi} from "../../maps/types/IPoi";
import {TransportPoiType} from "../../maps/types/PoiType";
import {OsmPoiClickHandler} from "../../maps/utils/create_get_osm_poi_marker";
import {getHasCountryPois} from "../../maps/utils/get_has_country_pois";
import {OfferDetailLocationSummary} from "../../offer/detail/components/OfferDetailLocationSummary";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {trackAmplitudePropertyAccordionExpansion, trackAmplitudePropertyExpandSection} from "../../tracking/amplitude/track_amplitude_property_expand_section";
import {useTrackAmplitudeMapComponentActionOnce} from "../../tracking/amplitude/use_track_amplitude_map_component_action_once";
import {ViewType} from "../../view_type/ViewType";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {OSM_MAP_CONTAINER} from "./PropertyDetailForSale";
import {PropertyViewSectionTracker} from "./PropertyViewSectionTracking";

interface IProps {
    offer: IPropertyDetailOffer;
    propertyId: number;
    geoPointCoordinates: [number, number];
    changeModalTab: (tab: OfferModalActiveTab.MAP, source?: GTMEventApplicationSourceSection) => void;
}

export const PropertyDetailLocation = (props: IProps) => {
    const {changeModalTab, geoPointCoordinates, offer, propertyId} = props;
    const mapComponentActionHandlers = useTrackAmplitudeMapComponentActionOnce({
        location: "property_page",
        itemId: offer.id,
        propertyId
    });

    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: offer.id,
            type: offer.type,
            stats: {
                ranges_area_min: offer.stats?.ranges_area_min,
                ranges_area_max: offer.stats?.ranges_area_max,
                rooms: offer.stats?.rooms
            }
        },
        showPropertyNumberOnMarker: true
    });

    const hasCountryPois = getHasCountryPois(offer.region.country);
    const {data: poiData, isLoading: isTransportPoiLoading} = useGetOfferDetailTransportPoiQuery(offer.id);

    const transportPoisFromApi = (poiData?.poi?.pois.transport || EMPTY_TRANSPORT_POIS) as Record<TransportPoiType, IPoi[]>;

    const {routes, isRoutesLoading} = useTransportPois(transportPoisFromApi, {
        longitude: geoPointCoordinates[0],
        latitude: geoPointCoordinates[1],
        radius: POI_DISTANCE_DEFAULT_VALUE_IN_METERS
    });

    const nearbyTransportPois = useMemo(() => filterTransportPoisByRadius(transportPoisFromApi, POI_DISTANCE_DEFAULT_VALUE_IN_METERS), [transportPoisFromApi]);

    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    const mapWrapperRef = useElementInteractionObserver<HTMLDivElement>({
        callback: () => {
            poiAnalytics.gtm.mapEvent({action: PoiGTMModalAction.MAP_FIRST_INTERACTION});
        },
        once: true
    });

    const openMapModal = (click?: {isFullscreenClick: boolean}) => {
        changeModalTab(
            OfferModalActiveTab.MAP,
            click?.isFullscreenClick ? GTMEventApplicationSourceSection.ZOOM_MAP : GTMEventApplicationSourceSection.BUTTON_MAP
        );
    };

    const onPoiClick = useCallback<OsmPoiClickHandler>(
        (poi, poiType, poiSubType) => {
            trackAmplitudeMapPoiClick({
                location: "property_page",
                poi,
                poiType,
                poiSubType,
                itemId: offer.id,
                propertyId
            });
        },
        [offer.id, propertyId]
    );

    return (
        <>
            <PropertyViewSectionTracker sectionName="map" />
            <div className={wrapperStyle}>
                <Text variant="headline_4" as="h2" className={headerStyle}>
                    Lokalizacja inwestycji
                </Text>

                <div ref={mapWrapperRef} className={mapWrapperStyle} data-id={OSM_MAP_CONTAINER} {...mapComponentActionHandlers}>
                    {canMapBeLoaded ? (
                        <OpenStreetMapsWithPoi
                            offer={offer}
                            viewType={ViewType.PROPERTY}
                            initialPoiTypes={defaultPoiTypes}
                            mapConfig={{scrollWheelZoom: false, fitBounds: true, maxZoom: 18}}
                            disablePoiSwitch
                            disableInitiallyOpenedPoiId
                            clusterMarkers
                            customPoiMarkers={offerAdditionalPois}
                            mapBottomSlot={mapBottomSlot}
                            className={mapCustomStyles}
                            onFullscreenClick={() => openMapModal({isFullscreenClick: true})}
                            onPoiClick={onPoiClick}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {hasCountryPois && (
                    <div className={contentWrapperStyle}>
                        <PropertyViewSectionTracker sectionName="public_transport" />
                        <OfferDetailLocationSummary
                            {...props}
                            routes={routes}
                            isRoutesLoading={isRoutesLoading}
                            nearbyTransportPois={nearbyTransportPois}
                            isTransportPoiLoading={isTransportPoiLoading}
                            onTransportRoutesExpand={() =>
                                trackAmplitudePropertyExpandSection({
                                    sectionName: "public_transport",
                                    itemId: offer.id,
                                    propertyId
                                })
                            }
                        />

                        <Button
                            variant="outlined_secondary"
                            onClick={() => {
                                trackAmplitudeButtonClick({
                                    buttonName: "check_location_map",
                                    value: "check_on_map",
                                    buttonCategory: "property_page",
                                    itemId: offer.id,
                                    propertyId
                                });
                                openMapModal({isFullscreenClick: false});
                            }}
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.CHECK_ON_MAP_BUTTON}
                        >
                            Sprawdź na mapie
                        </Button>
                    </div>
                )}
            </div>

            {offer.region.country === Country.POLAND && (
                <>
                    <PropertyViewSectionTracker sectionName="location" />
                    <Accordion
                        dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.MORE_ABOUT_LOCATION}
                        onChange={(expanded) =>
                            trackAmplitudePropertyAccordionExpansion(expanded, {
                                sectionName: "location",
                                itemId: offer.id,
                                propertyId
                            })
                        }
                    >
                        <Accordion.Header>
                            <Text as="h3" variant="headline_6">
                                Przeczytaj więcej o lokalizacji
                            </Text>
                        </Accordion.Header>

                        <Accordion.Content>
                            {offer.location_description && (
                                <Text className={cx(css({mt: 0}), preLine)} mb={2} variant="body_copy_1">
                                    {offer.location_description}
                                </Text>
                            )}

                            {offer.region.included_location?.description && (
                                <Text className={preLine} variant="body_copy_1">
                                    {offer.region.included_location.description}
                                </Text>
                            )}
                        </Accordion.Content>
                    </Accordion>
                </>
            )}
        </>
    );
};

const wrapperStyle = css({
    background: "var(--colors-white)",
    borderRadius: 2,
    py: 2,
    mb: 2,
    textAlign: "center",

    md: {
        py: 3
    }
});

const headerStyle = css({
    px: 1.5,
    mb: 2,

    md: {
        px: 3
    }
});

const mapWrapperStyle = css({
    position: "relative",
    height: "52rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    md: {
        height: "41rem",
        mx: 3
    }
});

const contentWrapperStyle = css({
    px: 1.5,

    md: {
        px: 3
    }
});

const preLine = css({
    whiteSpace: "pre-line"
});

const mapCustomStyles = css({
    "& .leaflet-div-icon": {
        backgroundColor: "transparent !important",
        border: "none !important"
    }
});
import {useCallback, useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Button} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Country} from "@pg-rp/common-types";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {useElementInteractionObserver} from "../../hooks/use_element_interaction_observer";
import {useGetOfferDetailTransportPoiQuery} from "../../maps/api/get_offer_detail_poi";
import {OpenStreetMapsWithPoi} from "../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {POI_DISTANCE_DEFAULT_VALUE_IN_METERS} from "../../maps/constants/pois_defaults";
import {useMapNearbyOffersPois} from "../../maps/hooks/use_map_nearby_offers_pois";
import {EMPTY_TRANSPORT_POIS, filterTransportPoisByRadius, useTransportPois} from "../../maps/hooks/use_transport_pois";
import {poiAnalytics, PoiGTMModalAction} from "../../maps/tracking/poi_analytics";
import {trackAmplitudeMapPoiClick} from "../../maps/tracking/track_amplitude_poi_click";
import {IPoi} from "../../maps/types/IPoi";
import {TransportPoiType} from "../../maps/types/PoiType";
import {OsmPoiClickHandler} from "../../maps/utils/create_get_osm_poi_marker";
import {getHasCountryPois} from "../../maps/utils/get_has_country_pois";
import {OfferDetailLocationSummary} from "../../offer/detail/components/OfferDetailLocationSummary";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {trackAmplitudePropertyAccordionExpansion, trackAmplitudePropertyExpandSection} from "../../tracking/amplitude/track_amplitude_property_expand_section";
import {useTrackAmplitudeMapComponentActionOnce} from "../../tracking/amplitude/use_track_amplitude_map_component_action_once";
import {ViewType} from "../../view_type/ViewType";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {OSM_MAP_CONTAINER} from "./PropertyDetailForSale";
import {PropertyViewSectionTracker} from "./PropertyViewSectionTracking";

interface IProps {
    offer: IPropertyDetailOffer;
    propertyId: number;
    geoPointCoordinates: [number, number];
    changeModalTab: (tab: OfferModalActiveTab.MAP, source?: GTMEventApplicationSourceSection) => void;
}

export const PropertyDetailLocation = (props: IProps) => {
    const {changeModalTab, geoPointCoordinates, offer, propertyId} = props;
    const mapComponentActionHandlers = useTrackAmplitudeMapComponentActionOnce({
        location: "property_page",
        itemId: offer.id,
        propertyId
    });

    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: offer.id,
            type: offer.type,
            stats: {
                ranges_area_min: offer.stats?.ranges_area_min,
                ranges_area_max: offer.stats?.ranges_area_max,
                rooms: offer.stats?.rooms
            }
        },
        showPropertyNumberOnMarker: true
    });

    const hasCountryPois = getHasCountryPois(offer.region.country);
    const {data: poiData, isLoading: isTransportPoiLoading} = useGetOfferDetailTransportPoiQuery(offer.id);

    const transportPoisFromApi = (poiData?.poi?.pois.transport || EMPTY_TRANSPORT_POIS) as Record<TransportPoiType, IPoi[]>;

    const {routes, isRoutesLoading} = useTransportPois(transportPoisFromApi, {
        longitude: geoPointCoordinates[0],
        latitude: geoPointCoordinates[1],
        radius: POI_DISTANCE_DEFAULT_VALUE_IN_METERS
    });

    const nearbyTransportPois = useMemo(() => filterTransportPoisByRadius(transportPoisFromApi, POI_DISTANCE_DEFAULT_VALUE_IN_METERS), [transportPoisFromApi]);

    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    const mapWrapperRef = useElementInteractionObserver<HTMLDivElement>({
        callback: () => {
            poiAnalytics.gtm.mapEvent({action: PoiGTMModalAction.MAP_FIRST_INTERACTION});
        },
        once: true
    });

    const openMapModal = (click?: {isFullscreenClick: boolean}) => {
        changeModalTab(
            OfferModalActiveTab.MAP,
            click?.isFullscreenClick ? GTMEventApplicationSourceSection.ZOOM_MAP : GTMEventApplicationSourceSection.BUTTON_MAP
        );
    };

    const onPoiClick = useCallback<OsmPoiClickHandler>(
        (poi, poiType, poiSubType) => {
            trackAmplitudeMapPoiClick({
                location: "property_page",
                poi,
                poiType,
                poiSubType,
                itemId: offer.id,
                propertyId
            });
        },
        [offer.id, propertyId]
    );

    return (
        <>
            <PropertyViewSectionTracker sectionName="map" />
            <div className={wrapperStyle}>
                <Text variant="headline_4" as="h2" className={headerStyle}>
                    Lokalizacja inwestycji
                </Text>

                <div ref={mapWrapperRef} className={mapWrapperStyle} data-id={OSM_MAP_CONTAINER} {...mapComponentActionHandlers}>
                    {canMapBeLoaded ? (
                        <OpenStreetMapsWithPoi
                            offer={offer}
                            viewType={ViewType.PROPERTY}
                            initialPoiTypes={defaultPoiTypes}
                            mapConfig={{scrollWheelZoom: false, fitBounds: true, maxZoom: 18}}
                            disablePoiSwitch
                            disableInitiallyOpenedPoiId
                            clusterMarkers
                            customPoiMarkers={offerAdditionalPois}
                            mapBottomSlot={mapBottomSlot}
                            className={mapCustomStyles}
                            onFullscreenClick={() => openMapModal({isFullscreenClick: true})}
                            onPoiClick={onPoiClick}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {hasCountryPois && (
                    <div className={contentWrapperStyle}>
                        <PropertyViewSectionTracker sectionName="public_transport" />
                        <OfferDetailLocationSummary
                            {...props}
                            routes={routes}
                            isRoutesLoading={isRoutesLoading}
                            nearbyTransportPois={nearbyTransportPois}
                            isTransportPoiLoading={isTransportPoiLoading}
                            onTransportRoutesExpand={() =>
                                trackAmplitudePropertyExpandSection({
                                    sectionName: "public_transport",
                                    itemId: offer.id,
                                    propertyId
                                })
                            }
                        />

                        <Button
                            variant="outlined_secondary"
                            onClick={() => {
                                trackAmplitudeButtonClick({
                                    buttonName: "check_location_map",
                                    value: "check_on_map",
                                    buttonCategory: "property_page",
                                    itemId: offer.id,
                                    propertyId
                                });
                                openMapModal({isFullscreenClick: false});
                            }}
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.CHECK_ON_MAP_BUTTON}
                        >
                            Sprawdź na mapie
                        </Button>
                    </div>
                )}
            </div>

            {offer.region.country === Country.POLAND && (
                <>
                    <PropertyViewSectionTracker sectionName="location" />
                    <Accordion
                        dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.MORE_ABOUT_LOCATION}
                        onChange={(expanded) =>
                            trackAmplitudePropertyAccordionExpansion(expanded, {
                                sectionName: "location",
                                itemId: offer.id,
                                propertyId
                            })
                        }
                    >
                        <Accordion.Header>
                            <Text as="h3" variant="headline_6">
                                Przeczytaj więcej o lokalizacji
                            </Text>
                        </Accordion.Header>

                        <Accordion.Content>
                            {offer.location_description && (
                                <Text className={cx(css({mt: 0}), preLine)} mb={2} variant="body_copy_1">
                                    {offer.location_description}
                                </Text>
                            )}

                            {offer.region.included_location?.description && (
                                <Text className={preLine} variant="body_copy_1">
                                    {offer.region.included_location.description}
                                </Text>
                            )}
                        </Accordion.Content>
                    </Accordion>
                </>
            )}
        </>
    );
};

const wrapperStyle = css({
    background: "var(--colors-white)",
    borderRadius: 2,
    py: 2,
    mb: 2,
    textAlign: "center",

    md: {
        py: 3
    }
});

const headerStyle = css({
    px: 1.5,
    mb: 2,

    md: {
        px: 3
    }
});

const mapWrapperStyle = css({
    position: "relative",
    height: "52rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    md: {
        height: "41rem",
        mx: 3
    }
});

const contentWrapperStyle = css({
    px: 1.5,

    md: {
        px: 3
    }
});

const preLine = css({
    whiteSpace: "pre-line"
});

const mapCustomStyles = css({
    "& .leaflet-div-icon": {
        backgroundColor: "transparent !important",
        border: "none !important"
    }
});
